/**
 * Payroll API Client
 * Frontend API calls for payroll management
 */

import apiClient from './client';

// ============================================================
// EMPLOYEE SALARIES
// ============================================================

export const getAllEmployeeSalaries = async (filters = {}) => {
  const response = await apiClient.get('/payroll/salaries', { params: filters });
  return response.data;
};

export const getEmployeeSalary = async (userId) => {
  const response = await apiClient.get(`/payroll/salaries/user/${userId}`);
  return response.data;
};

export const upsertEmployeeSalary = async (userId, salaryData) => {
  const response = await apiClient.put(`/payroll/salaries/user/${userId}`, salaryData);
  return response.data;
};

export const deleteEmployeeSalary = async (userId) => {
  const response = await apiClient.delete(`/payroll/salaries/user/${userId}`);
  return response.data;
};

// ============================================================
// PAYROLL RUNS
// ============================================================

export const getPayrollRuns = async (filters = {}) => {
  const response = await apiClient.get('/payroll/runs', { params: filters });
  return response.data;
};

export const getPayrollRun = async (runId) => {
  const response = await apiClient.get(`/payroll/runs/${runId}`);
  return response.data;
};

export const createPayrollRun = async (runData) => {
  const response = await apiClient.post('/payroll/runs', runData);
  return response.data;
};

export const processPayrollRun = async (runId) => {
  const response = await apiClient.post(`/payroll/runs/${runId}/process`);
  return response.data;
};

export const approvePayrollRun = async (runId) => {
  const response = await apiClient.post(`/payroll/runs/${runId}/approve`);
  return response.data;
};

export const markPayrollPaid = async (runId, paymentReference) => {
  const response = await apiClient.post(`/payroll/runs/${runId}/pay`, { paymentReference });
  return response.data;
};

export const updatePayrollRun = async (runId, updateData) => {
  const response = await apiClient.put(`/payroll/runs/${runId}`, updateData);
  return response.data;
};

// ============================================================
// PAYSLIPS
// ============================================================

export const getAllPayslips = async (filters = {}) => {
  const response = await apiClient.get('/payroll/payslips', { params: filters });
  return response.data;
};

export const getMyPayslips = async (limit = 12) => {
  const response = await apiClient.get('/payroll/my-payslips', { params: { limit } });
  return response.data;
};

export const getUserPayslips = async (userId, limit = 12) => {
  const response = await apiClient.get(`/payroll/payslips/user/${userId}`, { params: { limit } });
  return response.data;
};

export const getPayslip = async (payslipId) => {
  const response = await apiClient.get(`/payroll/payslips/${payslipId}`);
  return response.data;
};

// ============================================================
// LOANS
// ============================================================

export const getMyLoans = async () => {
  const response = await apiClient.get('/payroll/my-loans');
  return response.data;
};

export const getActiveLoans = async (params = {}) => {
  const response = await apiClient.get('/payroll/loans', { params });
  return response.data;
};

export const getUserLoans = async (userId) => {
  const response = await apiClient.get(`/payroll/loans/user/${userId}`);
  return response.data;
};

export const createLoan = async (loanData) => {
  const response = await apiClient.post('/payroll/loans', loanData);
  return response.data;
};

export const approveLoan = async (loanId, loanSettings) => {
  const response = await apiClient.post(`/payroll/loans/${loanId}/approve`, loanSettings);
  return response.data;
};

export const forwardLoan = async (loanId) => {
  const response = await apiClient.post(`/payroll/loans/${loanId}/forward`);
  return response.data;
};

export const getLoan = async (loanId) => {
  const response = await apiClient.get(`/payroll/loans/${loanId}`);
  return response.data;
};

export const cancelLoan = async (loanId) => {
  const response = await apiClient.post(`/payroll/loans/${loanId}/cancel`);
  return response.data;
};

export const rejectLoan = async (loanId, data = {}) => {
  const response = await apiClient.post(`/payroll/loans/${loanId}/reject`, data);
  return response.data;
};

export const updateLoan = async (loanId, data) => {
  const response = await apiClient.put(`/payroll/loans/${loanId}`, data);
  return response.data;
};

export const deleteLoan = async (loanId) => {
  const response = await apiClient.delete(`/payroll/loans/${loanId}`);
  return response.data;
};

export const reversePayrollRun = async (runId) => {
  const response = await apiClient.post(`/payroll/runs/${runId}/reverse`);
  return response.data;
};

export const deletePayrollRun = async (runId) => {
  const response = await apiClient.delete(`/payroll/runs/${runId}`);
  return response.data;
};

// ============================================================
// PDF DOWNLOAD
// ============================================================

export const downloadPayslipPDF = async (payslipId, payslipNumber) => {
  const response = await apiClient.get(`/payroll/payslips/${payslipId}/pdf`, {
    responseType: 'blob'
  });

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${payslipNumber || 'Payslip'}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default {
  getAllEmployeeSalaries,
  getEmployeeSalary,
  upsertEmployeeSalary,
  getPayrollRuns,
  getPayrollRun,
  createPayrollRun,
  processPayrollRun,
  approvePayrollRun,
  markPayrollPaid,
  reversePayrollRun,
  deletePayrollRun,
  getAllPayslips,
  getMyPayslips,
  getUserPayslips,
  getPayslip,
  getMyLoans,
  getActiveLoans,
  getUserLoans,
  createLoan,
  approveLoan,
  forwardLoan,
  cancelLoan,
  downloadPayslipPDF
};
